import { useState } from "react";
import { useForm } from "@mantine/form";
import {
  TextInput,
  PasswordInput,
  Text,
  Paper,
  Group,
  Button,
  Anchor,
  Stack,
  Container,
  Alert,
  Divider,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { IconInfoCircle, IconFingerprint } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import LangSelector from "./LangSelector";
import { socialLoginFactory } from "./socialLogin";
import { GoogleButton } from "../_Shared/GoogleButton";
import { useStore } from "../store";
import { arrayBufferToBase64Url, base64UrlDecode } from "./base64helpers";
import { notifications } from "@mantine/notifications";

// Reads an error response body as a human-readable string, tolerating both plain
// text (e.g. ASP.NET's NotFound(string) → text/plain) and JSON ProblemDetails
// ({ detail }) / { message } shapes. Never assume JSON — a blind .json() throws
// "Unexpected token ... is not valid JSON" on a plain-text body and masks the error.
async function readErrorDetail(response) {
  const raw = await response.text();
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.detail || parsed?.message || raw || response.statusText;
  } catch {
    return raw || response.statusText;
  }
}

export default function LoginForm(props) {
  const [isFetching, setIsFetching] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const authName = useStore((state) => state.authName);
  const [passkeyEmail, setPasskeyEmail] = useState(authName);
  const userLoggedIn = useStore((state) => state.userLoggedIn);
  const login = useStore((state) => state.login);

  const [passkeyLoginStep, setPasskeyLoginStep] = useState("initial");
  // Set when the server refuses password login because the tenant requires passkey sign-in — reveals
  // the passkey button even for a browser that hasn't signed in here before (no stored authName).
  const [forcePasskey, setForcePasskey] = useState(false);

  const handlePasskeyLogin = async () => {
    try {
      if (!passkeyEmail) {
        // First step - ask for email
        // console.debug("No email provided, asking for email");
        setPasskeyLoginStep("email");
        // console.debug("Passkey login step set to email");
        return;
      }

      setIsFetching("passkey");

      // Start passkey login process
      const optionsResponse = await fetch("/api/v1/auth/passkey/login/begin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: passkeyEmail, realm: props.realm }),
      });

      if (!optionsResponse.ok) {
        const detail = await readErrorDetail(optionsResponse);
        throw new Error(`Failed to start passkey authentication: ${detail}`);
      }

      const options = await optionsResponse.json();
      // console.debug("Received options from server:", options);

      // Try both approaches: with and without allowCredentials
      try {
        // First attempt: Try with explicit credentials
        // console.debug(
        //   "Attempting with conditional UI (discoverable credentials)"
        // );
        const conditionalMediationOptions = {
          publicKey: {
            challenge: base64UrlDecode(options.challenge),
            rpId: options.rpId,
            timeout: options.timeout,
            userVerification: options.userVerification || "preferred",
            allowCredentials: (options.allowCredentials || []).map((c) => ({
              id: base64UrlDecode(c.id),
              type: c.type,
              transports: ["internal"],
            })),
          },
          // mediation: "conditional"
        };

        const credential = await navigator.credentials.get(
          conditionalMediationOptions
        );
        // console.debug("Successfully got credential using explicit UI");

        // Process credential and complete login
        return processCredential(credential);
      } catch (conditionalError) {
        // console.debug("Conditional UI approach failed:", conditionalError);

        // Second attempt: Try with discoverable credentials
        // console.debug("Attempting with explicit credentials");
        const explicitOptions = {
          publicKey: {
            challenge: base64UrlDecode(options.challenge),
            rpId: options.rpId,
            timeout: options.timeout,
            userVerification: options.userVerification || "preferred",
            // allowCredentials: (options.allowCredentials || []).map((c) => ({
            //   id: base64UrlDecode(c.id),
            //   type: "public-key", // Always use "public-key", not c.type
            //   transports: ["internal", "usb", "ble", "nfc"], // Provide all possible transports
            // })),
          },
        };

        const credential = await navigator.credentials.get(explicitOptions);
        // console.debug("Successfully got credential using explicit credentials");

        // Process credential and complete login
        return processCredential(credential);
      }

      // Helper function to process credential and complete login
      async function processCredential(credential) {
        // console.debug("Processing credential", credential);

        // Convert credential for sending to server
        const authData = {
          id: credential.id,
          realm: props.realm,
          rawId: arrayBufferToBase64Url(credential.rawId),
          response: {
            authenticatorData: arrayBufferToBase64Url(
              credential.response.authenticatorData
            ),
            clientDataJSON: arrayBufferToBase64Url(
              credential.response.clientDataJSON
            ),
            signature: arrayBufferToBase64Url(credential.response.signature),
            userHandle: credential.response.userHandle
              ? arrayBufferToBase64Url(credential.response.userHandle)
              : null,
          },
          type: credential.type,
          clientExtensionResults: credential.getClientExtensionResults(),
        };

        // Complete authentication on server
        const loginResponse = await fetch(
          "/api/v1/auth/passkey/login/complete",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(authData),
          }
        );

        if (!loginResponse.ok) {
          const detail = await readErrorDetail(loginResponse);
          setErrorMessage(detail);
          throw new Error(`Failed to complete authentication: ${detail}`);
        }

        const loginResult = await loginResponse.json();
        // console.debug("Login successful:", loginResult);
        setErrorMessage(null);
        await userLoggedIn(loginResult);
        return loginResult;
      }
    } catch (error) {
      console.error("Passkey authentication error:", error);
      setErrorMessage(error.message);
      notifications.show({
        title: "Passkey Login Error",
        message: error.message,
        color: "red",
      });
    } finally {
      setIsFetching(null);
    }
  };

  const { t } = useTranslation("Auth");
  const [, setLocation] = useLocation();

  const socialLogin = socialLoginFactory(async (event) => {
    await userLoggedIn(event.data);
  });

  const form = useForm({
    initialValues: {
      emailAddress: "",
      realm: props.realm,
      password: "",
    },

    validate: {
      emailAddress: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)
          ? null
          : t("invalid_email"),
      // tenantSlug: (val) => (/^[a-z][a-z0-9_-]+$/.test(val) ? null : "Invalid slug"),
      password: (val) => (val.length < 8 ? t("invalid_password") : null),
    },
  });

  return (
    <Container size="xs" mt="xl">
      {errorMessage && (
        <Alert
          variant="light"
          color="red"
          title={t("login_failed")}
          mb="lg"
          icon={<IconInfoCircle />}
        >
          {errorMessage}
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder {...props}>
        <Group align="flex-start" justify="space-between">
          <Text size="lg" mb="xl" fw={500}>
            {t("title")}
          </Text>
          <LangSelector />
        </Group>
        {/* PASSKEY STEP: {passkeyLoginStep || "none"} */}
        {!props.hideGoogle && (
        <Group grow mb="md" mt="md">
          <GoogleButton
            //loading={isFetchingGoogle}
            loading={isFetching === "google"}
            disabled={isFetching}
            onClick={() => {
              setIsFetching("google");
              socialLogin("Google");
            }}
            radius="xl"
          >
            Google
          </GoogleButton>
        </Group>
        )}
        {(authName || forcePasskey) && (
          <Group grow mb="md" mt="md">
            <Button
              leftSection={<IconFingerprint size={16} />}
              variant={forcePasskey ? "filled" : "outline"}
              loading={isFetching === "passkey" && passkeyLoginStep}
              disabled={isFetching}
              onClick={handlePasskeyLogin}
              radius="xl"
            >
              Sign in with Passkey
            </Button>
          </Group>
        )}
        {passkeyLoginStep === "email" && (
          <Group grow mb="md" mt="md">
            <TextInput
              placeholder="Enter your email"
              value={passkeyEmail}
              onChange={(e) => setPasskeyEmail(e.currentTarget.value)}
            />
            <Button
              onClick={handlePasskeyLogin}
              disabled={!passkeyEmail}
              radius="xl"
            >
              Continue
            </Button>
          </Group>
        )}
        {(!props.hideGoogle || authName) && (
        <Divider
          label="Or continue with email"
          labelPosition="center"
          my="lg"
        />
        )}
        <form
          onSubmit={form.onSubmit((values) => {
            setIsFetching("email");
            setErrorMessage(null);

            login({
              ...values,
              scope: "mandatory-field",
              realm: values.realm,
              username: values.emailAddress,
            })
              .then(() => {
                setIsFetching(null);
              })
              .catch((err) => {
                console.error("Login error:", err);
                setIsFetching(null);
                if (err.code === "passkey_required") {
                  // Credentials were fine, but this tenant mandates passkey sign-in.
                  setErrorMessage(
                    t(
                      "passkey_required_login",
                      "Your organization requires passkey sign-in. Please sign in with your passkey.",
                    ),
                  );
                  setPasskeyEmail(values.emailAddress);
                  setForcePasskey(true);
                } else if (err.cause == 400 || err.cause == 401) {
                  setErrorMessage(t("incorrect_user_or_password"));
                } else if (err.cause == 429) {
                  setErrorMessage(t("rate_limit_exceeded"));
                } else {
                  setErrorMessage(t("unexpected_error") + err);
                }
              });
          })}
        >
          <Stack>
            <TextInput
              required
              label={t("email")}
              placeholder="john@example.com"
              value={form.values.emailAddress}
              onChange={(event) =>
                form.setFieldValue("emailAddress", event.currentTarget.value)
              }
              // tbd: might have to get login options onBlur ? But that leaks information
              // on whether the user is registered or not...
              error={form.errors.emailAddress && t("invalid_email")}
              radius="md"
            />

            <PasswordInput
              required
              label={t("password")}
              placeholder={t("your_password")}
              value={form.values.password}
              onChange={(event) =>
                form.setFieldValue("password", event.currentTarget.value)
              }
              error={form.errors.password}
              radius="md"
            />
          </Stack>

          <Group justify="space-between" mt="xl">
            <Stack>
              {!props.hideRegister && (
                <Anchor
                  c="dimmed"
                  component={Link}
                  href="/auth/register"
                  size="xs"
                >
                  Don't have an account? Register!
                </Anchor>
              )}
              <Anchor c="dimmed" component={Link} href="/auth/reset" size="xs">
                {t("forgot_password")}
              </Anchor>
            </Stack>
            <Group>
              {/* <Button
                leftSection={<IconFingerprint size={16} />}
                variant="outline"
                loading={isFetching === "passkey" && passkeyLoginStep}
                disabled={isFetching}
                onClick={handlePasskeyLogin}
                radius="xl"
              >
                Sign in with Passkey
              </Button> */}
              <Button
                loading={isFetching === "email"}
                disabled={isFetching}
                type="submit"
                radius="xl"
              >
                {t("login")}
              </Button>
            </Group>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
